import type { VectorEdge, VectorNetwork, VectorVertex } from "@/document/model/vectorNetwork";
import { edgeBezierControls } from "@/document/operations/vectorGeometry";
import type { Point } from "@/lib/math/rotation";

const CURVE_SAMPLES = 96;
const EPSILON = 1e-6;

interface ChainEdge {
  edge: VectorEdge;
  forward: boolean;
}

interface ArcSegment {
  length: number;
  pointAtDistance(distance: number): Point;
  tangentAtDistance(distance: number): Point;
  edgeBetweenDistances(fromDistance: number, toDistance: number): SlicedEdge | undefined;
}

interface ArcSample {
  t: number;
  length: number;
  point: Point;
}

interface SlicedEdge {
  from: Point;
  to: Point;
  fromHandle?: Point;
  toHandle?: Point;
}

export function pointOnFollowPath(network: VectorNetwork, progress: number): Point | undefined {
  const path = openChainArcPath(network);
  if (!path) {
    return undefined;
  }

  let distance = Math.max(0, Math.min(1, progress)) * path.totalLength;
  for (const segment of path.segments) {
    if (distance <= segment.length) {
      return segment.pointAtDistance(distance);
    }
    distance -= segment.length;
  }
  return path.segments.at(-1)?.pointAtDistance(path.segments.at(-1)!.length);
}

export function tangentOnFollowPath(network: VectorNetwork, progress: number): Point | undefined {
  const path = openChainArcPath(network);
  if (!path) {
    return undefined;
  }

  let distance = Math.max(0, Math.min(1, progress)) * path.totalLength;
  for (const segment of path.segments) {
    if (distance <= segment.length) {
      return segment.tangentAtDistance(distance);
    }
    distance -= segment.length;
  }
  const last = path.segments.at(-1);
  return last?.tangentAtDistance(last.length);
}

export function strokePathNetwork(network: VectorNetwork, progress: number, inverse: boolean, erase: boolean): VectorNetwork | undefined {
  const path = openChainArcPath(network);
  if (!path) {
    return undefined;
  }

  const amount = Math.max(0, Math.min(1, progress));
  const orientedStart = 0;
  const orientedEnd = erase ? 1 - amount : amount;
  const startProgress = inverse ? 1 - orientedEnd : orientedStart;
  const endProgress = inverse ? 1 - orientedStart : orientedEnd;
  return sliceOpenChainNetwork(network, path.segments, Math.min(startProgress, endProgress), Math.max(startProgress, endProgress));
}

function openChainArcPath(network: VectorNetwork): { segments: ArcSegment[]; totalLength: number } | undefined {
  const chain = orderedOpenChain(network);
  if (!chain) {
    return undefined;
  }

  const vertexById = new Map(network.vertices.map(vertex => [vertex.id, vertex] as const));
  const segments = chain.flatMap(ref => {
    const from = vertexById.get(ref.forward ? ref.edge.from.vertexId : ref.edge.to.vertexId);
    const to = vertexById.get(ref.forward ? ref.edge.to.vertexId : ref.edge.from.vertexId);
    if (!from || !to) {
      return [];
    }
    const fromHandle = ref.forward ? ref.edge.from.handle : ref.edge.to.handle;
    const toHandle = ref.forward ? ref.edge.to.handle : ref.edge.from.handle;
    return [arcSegment(from, to, fromHandle, toHandle)];
  }).filter((segment): segment is ArcSegment => {
    if (!segment) {
      return false;
    }
    return segment.length > EPSILON;
  });

  const totalLength = segments.reduce((sum, segment) => sum + segment.length, 0);
  return totalLength > EPSILON ? { segments, totalLength } : undefined;
}

function sliceOpenChainNetwork(network: VectorNetwork, segments: ArcSegment[], startProgress: number, endProgress: number): VectorNetwork {
  const totalLength = segments.reduce((sum, segment) => sum + segment.length, 0);
  const startDistance = Math.max(0, Math.min(1, startProgress)) * totalLength;
  const endDistance = Math.max(0, Math.min(1, endProgress)) * totalLength;
  if (endDistance - startDistance <= EPSILON) {
    return { ...structuredClone(network), vertices: [], edges: [], regions: [] };
  }

  const slicedEdges: SlicedEdge[] = [];
  let cursor = 0;
  for (const segment of segments) {
    const segmentStart = cursor;
    const segmentEnd = cursor + segment.length;
    const from = Math.max(startDistance, segmentStart);
    const to = Math.min(endDistance, segmentEnd);
    if (to - from > EPSILON) {
      const edge = segment.edgeBetweenDistances(from - segmentStart, to - segmentStart);
      if (edge) {
        slicedEdges.push(edge);
      }
    }
    cursor = segmentEnd;
    if (cursor >= endDistance) {
      break;
    }
  }

  const vertices: VectorVertex[] = [];
  const edges: VectorEdge[] = [];
  for (const [index, edge] of slicedEdges.entries()) {
    const fromId = index === 0 ? "strokePathVertex0" : vertices[vertices.length - 1]!.id;
    if (index === 0) {
      vertices.push({ id: fromId, x: edge.from.x, y: edge.from.y });
    }
    const toId = `strokePathVertex${index + 1}`;
    vertices.push({ id: toId, x: edge.to.x, y: edge.to.y });
    edges.push({
      id: `strokePathEdge${index}`,
      from: { vertexId: fromId, handle: edge.fromHandle },
      to: { vertexId: toId, handle: edge.toHandle },
      directed: false,
    });
  }

  return { ...structuredClone(network), vertices, edges, regions: [] };
}

function orderedOpenChain(network: VectorNetwork): ChainEdge[] | undefined {
  if (network.edges.length === 0) {
    return undefined;
  }

  const vertexById = new Map(network.vertices.map(vertex => [vertex.id, vertex] as const));
  const adjacency = new Map<string, Array<{ edge: VectorEdge; otherVertexId: string }>>();
  const usedVertexIds = new Set<string>();

  for (const edge of network.edges) {
    const from = vertexById.get(edge.from.vertexId);
    const to = vertexById.get(edge.to.vertexId);
    if (!from || !to || from.id === to.id) {
      return undefined;
    }
    usedVertexIds.add(from.id);
    usedVertexIds.add(to.id);
    adjacency.set(from.id, [...(adjacency.get(from.id) ?? []), { edge, otherVertexId: to.id }]);
    adjacency.set(to.id, [...(adjacency.get(to.id) ?? []), { edge, otherVertexId: from.id }]);
  }

  if (usedVertexIds.size !== network.edges.length + 1) {
    return undefined;
  }

  const endpoints = [...usedVertexIds].filter(vertexId => (adjacency.get(vertexId)?.length ?? 0) === 1);
  const hasOnlyChainDegrees = [...usedVertexIds].every(vertexId => {
    const degree = adjacency.get(vertexId)?.length ?? 0;
    return degree === 1 || degree === 2;
  });
  if (endpoints.length !== 2 || !hasOnlyChainDegrees) {
    return undefined;
  }

  const ordered: ChainEdge[] = [];
  const visitedEdges = new Set<string>();
  let currentVertexId = endpoints[0]!;
  let previousEdgeId: string | undefined;

  while (ordered.length < network.edges.length) {
    const next = (adjacency.get(currentVertexId) ?? []).find(candidate => candidate.edge.id !== previousEdgeId && !visitedEdges.has(candidate.edge.id));
    if (!next) {
      return undefined;
    }
    visitedEdges.add(next.edge.id);
    ordered.push({ edge: next.edge, forward: next.edge.from.vertexId === currentVertexId });
    previousEdgeId = next.edge.id;
    currentVertexId = next.otherVertexId;
  }

  return currentVertexId === endpoints[1] && visitedEdges.size === network.edges.length ? ordered : undefined;
}

function arcSegment(from: Point, to: Point, fromHandle: Point | undefined, toHandle: Point | undefined): ArcSegment | undefined {
  const controls = edgeBezierControls(from, to, fromHandle, toHandle);
  if (!controls) {
    const length = distanceBetween(from, to);
    return length > EPSILON
      ? {
          length,
          pointAtDistance: distance => lerpPoint(from, to, Math.max(0, Math.min(1, distance / length))),
          tangentAtDistance: () => normalizedDirection(from, to),
          edgeBetweenDistances: (fromDistance, toDistance) => {
            const start = Math.max(0, Math.min(1, fromDistance / length));
            const end = Math.max(0, Math.min(1, toDistance / length));
            return end - start > EPSILON
              ? { from: lerpPoint(from, to, start), to: lerpPoint(from, to, end) }
              : undefined;
          },
        }
      : undefined;
  }

  const samples: ArcSample[] = [{ t: 0, length: 0, point: controls.p0 }];
  let totalLength = 0;
  let previous = controls.p0;
  for (let i = 1; i <= CURVE_SAMPLES; i += 1) {
    const t = i / CURVE_SAMPLES;
    const point = cubicPoint(controls.p0, controls.p1, controls.p2, controls.p3, t);
    totalLength += distanceBetween(previous, point);
    samples.push({ t, length: totalLength, point });
    previous = point;
  }

  if (totalLength <= EPSILON) {
    return undefined;
  }

  return {
    length: totalLength,
    pointAtDistance(distance) {
      return cubicPointAtDistance(controls, samples, distance);
    },
    tangentAtDistance(distance) {
      const t = cubicTAtDistance(samples, distance);
      return normalizedCubicTangent(controls, t);
    },
    edgeBetweenDistances(fromDistance, toDistance) {
      const start = cubicTAtDistance(samples, fromDistance);
      const end = cubicTAtDistance(samples, toDistance);
      if (end - start <= EPSILON) {
        return undefined;
      }
      const sliced = cubicBetween(controls, start, end);
      return {
        from: sliced.p0,
        fromHandle: sliced.p1,
        toHandle: sliced.p2,
        to: sliced.p3,
      };
    },
  };
}

function cubicPointAtDistance(controls: NonNullable<ReturnType<typeof edgeBezierControls>>, samples: ArcSample[], distance: number): Point {
  const t = cubicTAtDistance(samples, distance);
  return cubicPoint(controls.p0, controls.p1, controls.p2, controls.p3, t);
}

function cubicTAtDistance(samples: ArcSample[], distance: number): number {
  const totalLength = samples.at(-1)?.length ?? 0;
  const clamped = Math.max(0, Math.min(totalLength, distance));
  const index = firstSampleAtOrAfter(samples, clamped);
  const right = samples[index] ?? samples.at(-1)!;
  const left = samples[Math.max(0, index - 1)] ?? samples[0]!;
  const span = right.length - left.length;
  return span <= EPSILON ? right.t : left.t + ((clamped - left.length) / span) * (right.t - left.t);
}

function cubicBetween(controls: NonNullable<ReturnType<typeof edgeBezierControls>>, start: number, end: number): NonNullable<ReturnType<typeof edgeBezierControls>> {
  if (start <= EPSILON && end >= 1 - EPSILON) {
    return controls;
  }
  if (start <= EPSILON) {
    return splitCubic(controls, end).left;
  }
  if (end >= 1 - EPSILON) {
    return splitCubic(controls, start).right;
  }
  const left = splitCubic(controls, end).left;
  return splitCubic(left, start / end).right;
}

function splitCubic(controls: NonNullable<ReturnType<typeof edgeBezierControls>>, t: number): {
  left: NonNullable<ReturnType<typeof edgeBezierControls>>;
  right: NonNullable<ReturnType<typeof edgeBezierControls>>;
} {
  const p01 = lerpPoint(controls.p0, controls.p1, t);
  const p12 = lerpPoint(controls.p1, controls.p2, t);
  const p23 = lerpPoint(controls.p2, controls.p3, t);
  const p012 = lerpPoint(p01, p12, t);
  const p123 = lerpPoint(p12, p23, t);
  const p0123 = lerpPoint(p012, p123, t);
  return {
    left: { p0: controls.p0, p1: p01, p2: p012, p3: p0123 },
    right: { p0: p0123, p1: p123, p2: p23, p3: controls.p3 },
  };
}

function firstSampleAtOrAfter(samples: ArcSample[], length: number): number {
  let low = 0;
  let high = samples.length - 1;
  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (samples[mid]!.length < length) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  return low;
}

function cubicPoint(p0: Point, p1: Point, p2: Point, p3: Point, t: number): Point {
  const mt = 1 - t;
  const mt2 = mt * mt;
  const t2 = t * t;
  return {
    x: mt2 * mt * p0.x + 3 * mt2 * t * p1.x + 3 * mt * t2 * p2.x + t2 * t * p3.x,
    y: mt2 * mt * p0.y + 3 * mt2 * t * p1.y + 3 * mt * t2 * p2.y + t2 * t * p3.y,
  };
}

function normalizedCubicTangent(
  controls: NonNullable<ReturnType<typeof edgeBezierControls>>,
  t: number,
): Point {
  const mt = 1 - t;
  const tangent = {
    x: 3 * mt * mt * (controls.p1.x - controls.p0.x)
      + 6 * mt * t * (controls.p2.x - controls.p1.x)
      + 3 * t * t * (controls.p3.x - controls.p2.x),
    y: 3 * mt * mt * (controls.p1.y - controls.p0.y)
      + 6 * mt * t * (controls.p2.y - controls.p1.y)
      + 3 * t * t * (controls.p3.y - controls.p2.y),
  };
  const length = Math.hypot(tangent.x, tangent.y);
  if (length > EPSILON) {
    return { x: tangent.x / length, y: tangent.y / length };
  }

  const delta = 1 / CURVE_SAMPLES;
  const before = cubicPoint(controls.p0, controls.p1, controls.p2, controls.p3, Math.max(0, t - delta));
  const after = cubicPoint(controls.p0, controls.p1, controls.p2, controls.p3, Math.min(1, t + delta));
  return normalizedDirection(before, after);
}

function normalizedDirection(from: Point, to: Point): Point {
  const length = distanceBetween(from, to);
  return length > EPSILON
    ? { x: (to.x - from.x) / length, y: (to.y - from.y) / length }
    : { x: 1, y: 0 };
}

function lerpPoint(from: Point, to: Point, t: number): Point {
  return {
    x: from.x + (to.x - from.x) * t,
    y: from.y + (to.y - from.y) * t,
  };
}

function distanceBetween(from: Point, to: Point): number {
  return Math.hypot(to.x - from.x, to.y - from.y);
}
